import { FC } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useAppSelector } from '../../../main/hooks/hooks';
import { selectIsLoading, selectFullscreenLoading } from '../../../main/slices/utilSlice';
import variables from '../../../assets/styles/variables.module.scss';

// CHKM-4023 round 2 (Item 3): must equal `.component-wrapper`'s clientHeight
// exactly (Layout.scss: `calc(100vh - $header-height - $footer-height)`), so
// an in-flow `position: sticky` box sized to it contributes zero scrollable
// overflow beyond the scrollport it sits in. The round-1 shape (`height:
// '100vh'` with a canceling `marginBottom: '-100vh'`) was NOT equivalent: per
// CSS Position 3 §3.4, a sticky box's *unshifted* flow position still
// contributes its full border box to the ancestor's scrollable overflow
// region, and a negative *margin* shifts siblings but cannot retract that
// contribution — so `.component-wrapper` gained an 80px phantom scroll range
// (and a flashing scrollbar) on every page shorter than 100vh, i.e. exactly
// the pages that normally have no scrollbar at all. Safe only because
// `LoadingIcon` has exactly one production render site (`Layout.tsx:47`),
// always inside `.component-wrapper` — coupling this component to Layout's
// height formula is the acknowledged trade-off (tech spec, Item 3).
const BLOCKER_HEIGHT = `calc(100vh - ${variables.header_height} - ${variables.footer_height})`;

const LoadingIcon: FC = () => {
    const isLoading = useAppSelector(selectIsLoading);
    const isFullscreenLoading = useAppSelector(selectFullscreenLoading);
    if (!isLoading) return null;
    return (
        <>
            {/* Blocking layer (CHKM-4023). Intercepts pointer events over page content
                while a non-fullscreen fetch is in flight. Kept as its own layer, separate
                from the blur/spinner below, so it never has to compete with an open MUI
                Dialog (theme.zIndex.modal === 1300) for stacking order: the maximum
                z-index used by any in-wrapper page content is 999
                (src/pages/ATAI/ATAI.scss), so 1000 blocks all page content while every
                Dialog/Menu/Popover (portalled to document.body at 1300+) stays clickable.
                `position: sticky` sized to exactly `.component-wrapper`'s own clientHeight
                (`BLOCKER_HEIGHT`, Item 3) keeps this pinned to the visible slice of
                `.component-wrapper` (its nearest scrolling ancestor) at any scroll
                position, instead of scrolling out of view the way
                `position: absolute; inset: 0` does once the page is scrolled — while
                contributing zero scrollable overflow of its own. Fullscreen loading has
                no page content beneath it to block, so this layer is skipped entirely in
                that mode. */}
            {!isFullscreenLoading && (
                <Box
                    data-testid="loading-overlay-blocker"
                    className="loading-icon-blocker"
                    sx={{
                        position: 'sticky',
                        top: 0,
                        height: BLOCKER_HEIGHT,
                        marginBottom: `calc(-1 * (${BLOCKER_HEIGHT}))`,
                        pointerEvents: 'auto',
                        zIndex: 1000,
                    }}
                />
            )}
            {/* Blur + spinner layer. Purely visual — it never blocks clicks itself
                (pointerEvents: 'none' in both modes; the blocking layer above owns that
                responsibility in non-fullscreen mode). Stays above every MUI Dialog in
                both modes so an open Dialog can never hide the spinner behind its paper
                or backdrop (CHKM-4023 round 2), and above the fullscreen graph-preview
                dialog specifically so its close button remains visible and reachable
                (AC4 / CHKM-3863). CHKM-4023 round 2 (Item 9): in non-fullscreen mode this
                used to be `position: absolute; inset: 0`, which anchors to the top of the
                scrollable *content*, not the visible viewport — so on a scrolled page the
                spinner scrolled out of view even though the blocking layer above (fixed to
                the scrollport via `sticky`) stayed put, leaving blocked-but-unblurred,
                spinner-less content visible. Given the same sticky/measured treatment as
                the blocking layer here so both layers track the scrollport together.
                Fullscreen mode is unaffected — it has no scrollable ancestor to escape, so
                it keeps `position: fixed; inset: 0` exactly as before. */}
            <Box
                className="loading-icon-wrapper loading-indicator"
                data-testid="loading-overlay"
                sx={isFullscreenLoading ? {
                    position: 'fixed',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(2px)',
                    zIndex: (theme) => theme.zIndex.modal + 1,
                    pointerEvents: 'none',
                } : {
                    position: 'sticky',
                    top: 0,
                    height: BLOCKER_HEIGHT,
                    marginBottom: `calc(-1 * (${BLOCKER_HEIGHT}))`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(2px)',
                    zIndex: (theme) => theme.zIndex.modal + 1,
                    pointerEvents: 'none',
                }}>
                <CircularProgress size={48} />
            </Box>
        </>
    );
};

export default LoadingIcon;
